import { translate, type TFunc } from "../i18n/i18n";

// --- Internal building blocks ---
const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 60 * SECONDS_IN_MINUTE; // 3600
const SECONDS_IN_DAY = 24 * SECONDS_IN_HOUR; // 86400

// --- Public semantic constants ---
export const THIRTY_MINUTES = 30 * SECONDS_IN_MINUTE;
export const ONE_HOUR = SECONDS_IN_HOUR;
export const ONE_DAY = SECONDS_IN_DAY;

/**
 * Converts a total number of seconds into a readable time string format (e.g., "2 hr 39 min").
 * It specifically ignores seconds and focuses on minutes and hours.
 *
 * @param totalSeconds The total duration in seconds (must be a non-negative number).
 * @param t A translate function (from useT) for the "hr"/"min"/"N/A" labels; pass
 *          it at display sites so the value re-renders on a language switch.
 *          Defaults to the current language.
 * @returns A formatted string representing the time, or "N/A" if no duration is provided,
 *          or "0 min" if the duration is negligible.
 * @throws Error if the input is negative.
 */
export const formatDuration = (
  totalSeconds?: number,
  t: TFunc = translate,
): string => {
  if (!totalSeconds) {
    return t("common.na");
  }

  if (totalSeconds < 0) {
    throw new Error("Cooking duration cannot be negative.");
  }

  const zero = `0 ${t("units.min")}`;
  if (totalSeconds === 0) {
    return zero;
  }

  // 1. Calculate Hours
  const hours = Math.floor(totalSeconds / SECONDS_IN_HOUR);

  // 2. Calculate remaining seconds after extracting hours
  const remainingSeconds = totalSeconds % SECONDS_IN_HOUR;

  // 3. Calculate Minutes from the remainder
  const minutes = Math.floor(remainingSeconds / SECONDS_IN_MINUTE);

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} ${t("units.hr")}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes} ${t("units.min")}`);
  }

  // If duration is less than 1 minute, return "0 min" as the minimum displayed unit
  if (parts.length === 0) {
    return zero;
  }

  return parts.join(" ");
};

/**
 * Defines the structure for time components, allowing null or undefined.
 */
export interface TimeComponents {
  days?: number | null;
  hours?: number | null;
  minutes?: number | null;
}

/**
 * Converts time components (days, hours, minutes) into total seconds.
 *
 * If any component is null, undefined, NaN, or missing, it is treated as 0.
 *
 * @param time The object containing the time components.
 * @returns The total time in seconds (number).
 */
export const convertToSeconds = (time: TimeComponents): number => {
  /**
   * Safely extracts a time component, returning 0 for any non-finite value
   * (null, undefined, NaN, Infinity, -Infinity).
   *
   * @param value The component value (e.g., time.days).
   * @returns The sanitized number (0 if invalid).
   */
  const safeExtract = (value: number | null | undefined): number => {
    return Number.isFinite(value) ? (value as number) : 0;
  };

  const days = safeExtract(time.days);
  const hours = safeExtract(time.hours);
  const minutes = safeExtract(time.minutes);

  const secondsFromDays = days * SECONDS_IN_DAY;
  const secondsFromHours = hours * SECONDS_IN_HOUR;
  const secondsFromMinutes = minutes * SECONDS_IN_MINUTE;

  return secondsFromDays + secondsFromHours + secondsFromMinutes;
};

/**
 * Converts a total number of seconds back into days, hours, and minutes.
 *
 * @param totalSeconds The total number of seconds (must be non-negative).
 * @returns An object containing the split time components.
 * @throws Error if totalSeconds is negative.
 */
export const secondsToTime = (totalSeconds: number): TimeComponents => {
  if (totalSeconds < 0) {
    throw new Error("Input totalSeconds must be a non-negative number.");
  }

  // Discard any fractional seconds
  let remainingSeconds = Math.floor(totalSeconds);

  // 1. Calculate Days
  const days = Math.floor(remainingSeconds / SECONDS_IN_DAY);
  remainingSeconds %= SECONDS_IN_DAY;

  // 2. Calculate Hours
  const hours = Math.floor(remainingSeconds / SECONDS_IN_HOUR);
  remainingSeconds %= SECONDS_IN_HOUR;

  // 3. Calculate Minutes
  const minutes = Math.floor(remainingSeconds / SECONDS_IN_MINUTE);

  // Note: any leftover seconds (0–59) are intentionally discarded,
  // as this function only returns days, hours, and minutes.

  return { days, hours, minutes };
};
